import store from 'config/store'
import DEFAULT_AVATAR from 'images/agentDefaultAvatar.svg'
import { map } from 'ramda'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { isNilOrEmpty } from 'utils'

const DEFAULT_RECIPIENT = {
  id: null,
  name: '',
  avatarUrl: DEFAULT_AVATAR,
  bot: false
}

interface ResolvedRecipient {
  recipient: Recipient
}

interface Recipient {
  id: number,
  name: string,
  avatarUrl: string,
  bot?: boolean,
  publicProfileUrl?: string | null,
  title?: string | null,
  status?: string | null
}

const getAgent = (t, agentId) => {
  const {embed} = store.getState()
  const {team} = embed.configuration

  const getRecipientName = (recipient) => {
    if (!recipient) {
      return ''
    }

    if (!recipient.name || recipient.name === '') {
     return recipient.bot ? "Bot" : t('agent')
    }

    return recipient.name
  }

  const getRecipientAvatarUrl = (recipient) => {
    if (!recipient) { return DEFAULT_AVATAR }

    if (recipient.avatarUrl) {
      return recipient.avatarUrl
    }

    if (!recipient.bot) {
      return DEFAULT_AVATAR
    }

    return null
  }

  if (!agentId || isNilOrEmpty(team)) {
    return {
      recipient: DEFAULT_RECIPIENT
    }
  }

  const recipient = team.find(user => user.id === agentId)

  return {
    recipient: {
      ...recipient,
      avatarUrl: getRecipientAvatarUrl(recipient),
      name: getRecipientName(recipient)
    }
  }
}

/**
 * @summary memoized function to return full agent data for a list of agents
 * @param agentIds array of agentIds to fetch
 * @returns ResolvedRecipient[] - list of fully realized agent objects
 */
 export const useRecipientsResolution = (agentIds: number[]) : ResolvedRecipient[] => {
  const {t} = useTranslation()
  const memoizedGetAgents = useMemo<ResolvedRecipient[]>(()=>{
    return map((agentId)=>getAgent(t,agentId),agentIds)
  },[t,agentIds])

  return memoizedGetAgents
 }

 /**
  * @summary memoized function to return full agent data
  * @param agentId agentId to get full data object for
  * @returns ResolvedRecipient - the fully realized agent object
  */
export const useRecipientResolution = (agentId: number | null | undefined) : ResolvedRecipient  => {
  const { t } = useTranslation()
  const memoizedGetAgent = useMemo<ResolvedRecipient>(()=>{
    return getAgent(t, agentId)
  },[t,agentId])

    return memoizedGetAgent
}
